// Per-page renderer for "spend-distribution" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
import autoTable from 'jspdf-autotable';
import { NAVY, safeText, formatValue, fetchPageAI } from '../utils';
import { drawVerticalBarChart, drawBarChart } from '../charts';

export async function renderSpendDistribution(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  const [distRes, kraljicRes] = await Promise.all([
    api.get(`/analytics/spend-distribution?${qs}`),
    api.get(`/analytics/kraljic-matrix?${qs}`).catch(() => ({ data: { data: [], quadrant_counts: {}, quadrant_spend: {} } })),
  ]);
  const d = distRes.data;
  const supplierList = d.distribution || d.suppliers || [];
  const totalSpend = d.total_spend || 0;

  // ── Pareto (80/20) Analysis ──
  let cumPct = 0;
  let pareto80idx = supplierList.length;
  const paretoRows = supplierList.map((s, i) => {
    cumPct += s.spend_percentage || 0;
    if (cumPct >= 80 && pareto80idx === supplierList.length) pareto80idx = i + 1;
    return { ...s, cumulative_pct: cumPct };
  });
  const pareto80Spend = paretoRows.slice(0, pareto80idx).reduce((sum, s) => sum + (s.total_spend || 0), 0);
  const paretoText = `Pareto 80/20: ${pareto80idx} of ${supplierList.length} suppliers (${((pareto80idx / supplierList.length) * 100).toFixed(0)}%) account for 80% of spend (${cs}${pareto80Spend.toLocaleString()})`;

  // ── Kraljic Analysis ──
  const kraljicData = kraljicRes.data?.data || [];
  const qCounts = kraljicRes.data?.quadrant_counts || {};
  const qSpend = kraljicRes.data?.quadrant_spend || {};
  const kraljicText = Object.entries(qCounts).map(([q, c]) => `- ${q}: ${c} suppliers, ${cs}${(qSpend[q] || 0).toLocaleString()}`).join('\n');
  const strategicSuppliers = kraljicData.filter(k => k.quadrant === 'Strategic').slice(0, 5).map(k => `${k.supplier} (${cs}${k.spend?.toLocaleString()})`).join(', ');
  const bottleneckSuppliers = kraljicData.filter(k => k.quadrant === 'Bottleneck').slice(0, 5).map(k => k.supplier).join(', ');

  // ── AI Context with all three tabs ──
  const top10 = supplierList.slice(0, 10).map(s => `- ${s.supplier}: ${cs}${s.total_spend?.toLocaleString()} (${s.spend_percentage?.toFixed(1)}%, ${s.transaction_count} txns)`).join('\n');
  const dataContext = `Total Suppliers: ${d.total_suppliers}\nTotal Spend: ${cs}${totalSpend.toLocaleString()}\n\n--- DISTRIBUTION ---\nTop 10 Suppliers:\n${top10}\n\n--- PARETO (80/20) ---\n${paretoText}\nTail spend (remaining ${supplierList.length - pareto80idx} suppliers): ${cs}${(totalSpend - pareto80Spend).toLocaleString()} (${(100 - (pareto80Spend / totalSpend * 100)).toFixed(1)}%)\n\n--- KRALJIC MATRIX ---\nQuadrant Summary:\n${kraljicText}\nStrategic suppliers: ${strategicSuppliers || 'None'}\nBottleneck suppliers: ${bottleneckSuppliers || 'None'}`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  // ── Section 1: Distribution KPIs ──
  autoTable(doc, {
    startY: y,
    head: [['Total Suppliers', 'Total Spend', 'Pareto 80% Threshold', 'Tail Suppliers']],
    body: [[
      String(d.total_suppliers),
      formatValue(totalSpend),
      `${pareto80idx} suppliers`,
      `${supplierList.length - pareto80idx} suppliers`,
    ]],
    styles: { fontSize: 9, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── Section 2: Top Suppliers Chart & Table ──
  if (supplierList.length > 0) {
    const chartData = supplierList.slice(0, 10).map(s => ({ label: s.supplier, value: s.total_spend }));
    y = drawBarChart(doc, 14, y, pw - 28, 80, chartData, { title: 'Top 10 Suppliers by Spend' });

    autoTable(doc, {
      startY: y,
      head: [['Supplier', 'Spend', '% Total', 'Cumulative %', 'Transactions']],
      body: paretoRows.slice(0, 15).map(s => [
        s.supplier,
        formatValue(s.total_spend || 0),
        `${s.spend_percentage?.toFixed(1)}%`,
        `${s.cumulative_pct?.toFixed(1)}%`,
        String(s.transaction_count),
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Section 3: Pareto 80/20 Summary ──
  doc.addPage(); y = 15;
  doc.setFontSize(10); doc.setFont('Helvetica', 'bold'); doc.setTextColor(0, 31, 92);
  doc.text(safeText('Pareto (80/20) Analysis'), 14, y); y += 6;
  doc.setFontSize(8); doc.setFont('Helvetica', 'normal'); doc.setTextColor(60);
  doc.text(safeText(`${pareto80idx} of ${supplierList.length} suppliers account for 80% of total spend.`), 14, y); y += 4;
  doc.text(safeText(`Tail spend: ${supplierList.length - pareto80idx} suppliers contribute the remaining ${(100 - (pareto80Spend / totalSpend * 100)).toFixed(1)}%.`), 14, y); y += 8;

  // Pareto cumulative chart
  if (paretoRows.length > 0) {
    const paretoChartData = paretoRows.slice(0, 15).map(s => ({ label: s.supplier, value: s.cumulative_pct }));
    y = drawVerticalBarChart(doc, 14, y, pw - 28, 75, paretoChartData, { title: 'Cumulative Spend % by Supplier' });
  }

  // ── Section 4: Kraljic Matrix ──
  if (kraljicData.length > 0) {
    if (y > 160) { doc.addPage(); y = 15; }
    doc.setFontSize(10); doc.setFont('Helvetica', 'bold'); doc.setTextColor(0, 31, 92);
    doc.text(safeText('Kraljic Matrix Analysis'), 14, y); y += 7;

    // Quadrant summary with spend
    autoTable(doc, {
      startY: y,
      head: [['Quadrant', 'Suppliers', 'Total Spend', '% of Spend']],
      body: ['Strategic', 'Leverage', 'Bottleneck', 'Non-Critical'].filter(q => qCounts[q]).map(q => [
        q,
        String(qCounts[q] || 0),
        formatValue(qSpend[q] || 0),
        `${totalSpend ? ((qSpend[q] || 0) / totalSpend * 100).toFixed(1) : 0}%`,
      ]),
      styles: { fontSize: 8, halign: 'center' },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
      columnStyles: { 0: { fontStyle: 'bold' } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Quadrant spend chart
    const qChartData = ['Strategic', 'Leverage', 'Bottleneck', 'Non-Critical']
      .filter(q => qSpend[q])
      .map(q => ({ label: q, value: qSpend[q] }));
    if (qChartData.length > 0) {
      y = drawVerticalBarChart(doc, 14, y, pw - 28, 65, qChartData, { title: 'Spend by Kraljic Quadrant' });
    }

    // Detailed supplier-quadrant table
    if (y > 180) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Supplier', 'Quadrant', 'Spend', '% Total', 'Transactions', 'Risk']],
      body: kraljicData.slice(0, 25).map(k => [
        k.supplier,
        k.quadrant,
        formatValue(k.spend || 0),
        `${(k.spend_percentage || 0).toFixed(1)}%`,
        String(k.transaction_count || 0),
        k.risk_rating || '-',
      ]),
      styles: { fontSize: 6.5 },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  return { y, aiInsights, aiRecommendations };
}
